class SwiperCarousel extends HTMLElement {
  constructor() {
    super();

    this.id = this.dataset.id;
    this.swiperEl = this.querySelector(`.swiper-carousel`);
    this._initSwiper = this._initSwiper.bind(this);
    this._scheduleReinit = this._scheduleReinit.bind(this);
    this._onResize = this._onResize.bind(this);
    this._setupSwiperLoadingLogic();
    this.fullWidth = this.dataset.fullWidth === "true";
    this._reinitTimeout = null;
    this._onBreakpoint = null;
    window.addEventListener("resize", this._onResize);
  }

  _loadSwiperOnVisible = () => {
    // If the element is hidden (display: none), offsetParent returns null.
    // In this case, getBoundingClientRect() would return all zeros, incorrectly satisfying 'isAboveFold'.
    // We defer to IntersectionObserver, which will trigger once the element becomes visible (e.g. 'hidden' class removed).
    if (this.offsetParent === null) {
      const viewportHeight = window.innerHeight;
      window.swiperLoader.initWhenVisible(this, this._initSwiper, {
        rootMargin: `${viewportHeight}px`,
        threshold: 0,
      });
      return;
    }

    const isAboveFold = this.getBoundingClientRect().top < window.innerHeight;

    if (isAboveFold) {
      window.swiperLoader.initImmediately(this._initSwiper);
    } else {
      const viewportHeight = window.innerHeight;
      window.swiperLoader.initWhenVisible(this, this._initSwiper, {
        rootMargin: `${viewportHeight}px`,
        threshold: 0.1,
      });
    }
  };

  _setupSwiperLoadingLogic() {
    if (!this.swiperEl) return;

    if (window.swiperLoader) {
      this._loadSwiperOnVisible();
    } else {
      window.addEventListener("swiperloader:ready", this._loadSwiperOnVisible, { once: true });
    }
  }

  _onResize() {
    if (!this.swiper || !window.Swiper) return;
    this._scheduleReinit(window.Swiper);
  }

  _initSwiper(Swiper) {
    if (!Swiper) return;

    if (this.swiper) {
      if (this._onBreakpoint) {
        this.swiper.off("breakpoint", this._onBreakpoint);
      }
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    const swiperId = "swiper-carousel-" + this.id;
    const arrowLeft = this.querySelector(".swiper-button-prev");
    const arrowRight = this.querySelector(".swiper-button-next");
    if (arrowLeft && arrowRight) {
      arrowLeft.classList.add(`${swiperId}-arrow-left`);
      arrowRight.classList.add(`${swiperId}-arrow-right`);
    }
    let carouselLeftOffset = 0;
    let carouselRightOffset = 0;
    this.swiperEl.classList.add(swiperId);
    const containerEl = this.swiperEl.querySelector(".container");
    if (containerEl) {
      const containerRect = containerEl.getBoundingClientRect();
      const containerStyles = window.getComputedStyle(containerEl);
      const paddingLeft = Number.parseFloat(containerStyles.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(containerStyles.paddingRight) || 0;
      carouselLeftOffset = Math.max(0, containerRect.left) + paddingLeft;
      carouselRightOffset = Math.max(0, window.innerWidth - containerRect.right) + paddingRight;
    }
    const config = {
      slidesPerView: 1.5,
      centeredSlides: false,
      loop: false,
      infinite: false,
      spaceBetween: 0,
      observer: true,
      observeParents: true,
      setWrapperSize: true,
      slidesOffsetAfter: carouselRightOffset,
      slidesOffsetBefore: carouselLeftOffset,
      breakpoints: {
        320: {
          slidesPerView: 1.5,
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: 2.5,
          slidesPerGroup: 2,
        },
        940: {
          slidesPerView: 3.5,
          slidesPerGroup: 3,
        },
        1200: {
          slidesPerView: 4.5,
          slidesPerGroup: 4,
        },
        1600: {
          slidesPerView: 5.5,
          slidesPerGroup: 6,
        },
      },
      navigation: {
        nextEl: `.${swiperId}-arrow-right`,
        prevEl: `.${swiperId}-arrow-left`,
      }
    };
    this.swiper = new Swiper(`.${swiperId}`, config);
    this._onBreakpoint = () => this._scheduleReinit(Swiper);
    this.swiper.on("breakpoint", this._onBreakpoint);

    const slides = this.swiper.slides;
    if (!slides || slides.length === 0) return;
    const biggestHeight = Math.max(
      ...slides.map((s) => s.offsetHeight)
    );
    //create a css variable for the height
    this.style.setProperty("--slides-height", `${biggestHeight}px`);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._onResize);
  }

  _scheduleReinit(Swiper) {
    if (this._reinitTimeout) {
      clearTimeout(this._reinitTimeout);
    }

    this._reinitTimeout = setTimeout(() => {
      this._reinitTimeout = null;
      this._initSwiper(Swiper);
    }, 100);
  }

  // Public method to manually trigger initialization
  init() {
    this._loadSwiperOnVisible();
  }

  // Force initialization immediately, bypassing visibility checks
  initImmediately() {
    if (window.swiperLoader) {
      window.swiperLoader.initImmediately(this._initSwiper);
    }
  }
}

customElements.define("swiper-carousel", SwiperCarousel);
